import React, { useState, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import ModuleEditorBase from './ModuleEditorBase';
import DropZone from '../../../pages/courses/wizard/shared/DropZone';
import { uploadFile, useImageUrl } from '../../../services/uploads';
import { isEmpty, PENDING_PLACEHOLDER } from './contentPlaceholder';

interface ImageEmbedEditorProps {
  module: {
    id: string;
    title: string;
    type: string;
    details: {
      image_url?: string;
      alt_text?: string;
      caption?: string;
    };
  };
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: any) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
}

const MAX_FILE_SIZE_MB = 10;

export default function ImageEmbedEditor({
  module,
  sectionId,
  onUpdate,
  onDelete,
}: ImageEmbedEditorProps) {
  const [details, setDetails] = useState(module.details);
  const [caption, setCaption] = useState(module.details.caption || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setDetails(module.details);
    setCaption(module.details.caption || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id, module.details.image_url]);

  const sanitize = useCallback((value: string) => {
    return DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  }, []);

  useEffect(() => {
    const sanitizedCaption = sanitize(caption);

    if (sanitizedCaption !== (details.caption || '')) {
      const timeout = setTimeout(async () => {
        try {
          setIsSaving(true);
          setIsSaved(false);
          // alt_text mirrors the caption so the <img> stays described for
          // accessibility without exposing a separate field to the author.
          const next = { ...details, alt_text: sanitizedCaption, caption: sanitizedCaption };
          setDetails(next);
          await onUpdate(module.id, { details: next });
          setIsSaved(true);
          setTimeout(() => setIsSaved(false), 2000);
        } catch (error) {
          console.error('Failed to save image embed:', error);
        } finally {
          setIsSaving(false);
        }
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [caption, details, module.id, onUpdate, sanitize]);

  const displayImageUrl = isEmpty(details.image_url) ? null : details.image_url ?? null;
  const [previewUrl] = useImageUrl(displayImageUrl);

  const handleFilesSelected = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadResult = await uploadFile(
        {
          file,
          resource_type: 'module_file' as const,
          resource_id: module.id,
          section_id: sectionId,
        },
        {
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      const nextDetails = { ...details, image_url: uploadResult.key };
      setDetails(nextDetails);
      // Default the caption to the sanitized file name (without extension), but
      // only when the field is still empty — never clobber a user-typed value.
      // The debounced auto-save effect persists this once it lands in state.
      setCaption((prev) => prev || sanitize(file.name.replace(/\.[^/.]+$/, '')));
      await onUpdate(module.id, { details: nextDetails });
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 300);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this image?')) return;
    const nextDetails = { ...details, image_url: PENDING_PLACEHOLDER };
    setDetails(nextDetails);
    await onUpdate(module.id, { details: nextDetails });
  };

  return (
    <ModuleEditorBase
      moduleType={module.type}
      moduleTitle={module.title}
      onTitleChange={async (title) => await onUpdate(module.id, { title })}
      onDelete={async () => await onDelete(module.id)}
      isSaving={isSaving || uploading}
      isSaved={isSaved}
    >
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-semibold text-text-primary text-center mb-4">
            Upload File
          </h3>

          {isEmpty(details.image_url) ? (
            <DropZone
              onFilesSelected={handleFilesSelected}
              accept="image/jpeg,image/png,image/webp,image/gif"
              maxSizeMB={MAX_FILE_SIZE_MB}
              variant="media"
            />
          ) : (
            <div className="relative w-full rounded-2xl overflow-hidden group">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={caption || 'Content image'}
                  className="w-full max-h-[320px] object-contain bg-surface-primary"
                />
              ) : (
                <div className="w-full h-[180px] bg-surface-primary flex items-center justify-center text-text-disabled text-sm">
                  Loading preview...
                </div>
              )}
              <button
                type="button"
                onClick={handleRemove}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove image"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M1 1l10 10M11 1L1 11" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          )}

          {uploading && (
            <div className="mt-2">
              <div className="w-full bg-grey-200 dark:bg-grey-700 rounded-full h-2">
                <div
                  className="bg-primary-600 dark:bg-primary-350 h-2 rounded-full transition-[width] duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-text-secondary text-center">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Caption (optional)
          </label>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(sanitize(e.target.value))}
            placeholder="Figure 1: Python syntax example"
            maxLength={300}
            className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-surface-primary text-text-primary"
          />
          <p className="mt-1 text-xs text-text-secondary">
            {caption.length}/300 characters
          </p>
        </div>
      </div>
    </ModuleEditorBase>
  );
}
